<script setup lang="ts">
import { useSchoolStore } from '@/presentation/stores/school/school.ts'
import { computed, onMounted, ref } from 'vue'
import { storeToRefs } from 'pinia'
import CDashboardSchoolListItem from '@/presentation/components/dashboard/CDashboardSchoolListItem.vue'
import { useDashboardStore } from '@/presentation/stores/dashboard/dashboard.ts'
import { default as PMessage } from 'primevue/message'
import { default as PSkeleton } from 'primevue/skeleton'

// STORES
const schoolStore = useSchoolStore()
const dashboardStore = useDashboardStore()

// STORE REFS
const { schoolStoreIsLoading, schoolStoreError } = storeToRefs(schoolStore)
const { dashboardSchoolList } = storeToRefs(dashboardStore)

// LOCAL STATE
const localLoading = ref(false)
const localError = ref('')

// COMPUTED
const isLoading = computed(() => localLoading.value || schoolStoreIsLoading.value)

const isEmpty = computed(() => !isLoading.value && dashboardSchoolList.value.length === 0)

const hasError = computed(
  () => (schoolStoreError.value && schoolStoreError.value.hasError) || localError.value !== ''
)

const errorMessage = computed(
  () => localError.value || (schoolStoreError.value?.hasError ? schoolStoreError.value.message : '')
)

// METHODS
/**
 * Charge les écoles pour l'utilisateur courant
 */
const loadSchools = async () => {
  try {
    localLoading.value = true
    localError.value = ''

    await schoolStore.fetchSchoolsForMe()
  } catch (err) {
    localError.value =
      err instanceof Error ? err.message : 'Erreur lors du chargement des établissements'
    console.error('Erreur lors du chargement des écoles:', err)
  } finally {
    localLoading.value = false
  }
}

// LIFECYCLE
onMounted(async () => {
  await loadSchools()
})
</script>

<template>
  <div>
    <!-- Message d'erreur -->
    <p-message v-if="hasError" severity="error" class="mb-4 w-full">
      {{ errorMessage }}
    </p-message>

    <!-- État de chargement -->
    <div v-if="isLoading" class="flex flex-col gap-2">
      <p-skeleton v-for="i in 3" :key="i" class="mb-2" height="6rem" />
    </div>

    <!-- État vide -->
    <div v-else-if="isEmpty" class="flex flex-col items-center justify-center p-8">
      <i class="pi pi-building text-surface-500 dark:text-surface-300 mb-4 text-4xl"></i>
      <h3 class="mt-0 mb-2">Aucun établissement</h3>
      <p class="text-surface-700 dark:text-surface-100 text-center">
        Vous n'avez pas encore d'établissements associés.
      </p>
    </div>

    <!-- Liste des établissements -->
    <div class="grid grid-cols-12 gap-4" v-else>
      <c-dashboard-school-list-item
        v-for="school in dashboardSchoolList"
        :key="`school_${school.id}`"
        :school="school"
      />
    </div>
  </div>
</template>
